const MAX_CAUSE_DEPTH = 8;

export interface ErrorDetail {
  message: string;
  name?: string;
  stack?: string;
  cause?: string | ErrorDetail;
}

export function isErrorDetail(value: unknown): value is ErrorDetail {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as ErrorDetail).message === "string"
  );
}

/**
 * Wraps a thrown value in a form that round-trips across a `postMessage`
 * boundary while preserving the originating error's `name`, `stack`, and
 * `cause` chain.
 *
 * Worker side: `SerializableError.from(err).toJSON()` produces an
 * `ErrorDetail` POJO suitable for `postMessage`.
 *
 * Main-thread side: `SerializableError.from(event.data.error).toError()`
 * reconstructs a real `Error` whose `name` (e.g. `AbortError`, `TypeError`),
 * `stack`, and `cause` come from the worker, so Sentry's default grouper can
 * split unrelated worker failures into distinct issues and its
 * `chained_exception` representation can show the full cause chain.
 *
 * `cause` is recursive: each link is either a `string` (worker reader modules
 * commonly throw `new Error(msg, { cause: "inner detail" })`) or another
 * `ErrorDetail` (a chained `Error`). Recursion is bounded by
 * `MAX_CAUSE_DEPTH` to defend against pathological chains.
 */
export class SerializableError {
  /**
   * Polymorphic factory accepting a native `Error`, an `ErrorDetail` wire
   * payload, an existing `SerializableError`, or any other value (whose
   * `String(...)` form becomes the message).
   */
  static from(input: unknown): SerializableError {
    return SerializableError.fromInternal(input, 0);
  }

  private static fromInternal(
    input: unknown,
    depth: number,
  ): SerializableError {
    if (input instanceof SerializableError) {
      return new SerializableError(
        input.message,
        input.name,
        input.stack,
        input.cause,
      );
    }
    if (input instanceof Error) {
      return new SerializableError(
        input.message,
        input.name,
        input.stack,
        SerializableError.normalizeCause(input.cause, depth),
      );
    }
    if (isErrorDetail(input)) {
      return new SerializableError(
        input.message,
        input.name,
        input.stack,
        SerializableError.normalizeCause(input.cause, depth),
      );
    }
    return new SerializableError(String(input));
  }

  private static normalizeCause(
    cause: unknown,
    depth: number,
  ): string | SerializableError | undefined {
    if (cause === undefined || cause === null) {
      return undefined;
    }
    if (depth >= MAX_CAUSE_DEPTH) {
      return "<cause chain truncated>";
    }
    if (typeof cause === "string") {
      return cause;
    }
    if (cause instanceof SerializableError) {
      return cause;
    }
    if (cause instanceof Error || isErrorDetail(cause)) {
      return SerializableError.fromInternal(cause, depth + 1);
    }
    // Fallback for non-Error, non-ErrorDetail, non-string cause values (a
    // plain object, array, number, etc.). JSON-encode rather than coerce via
    // `String(...)`, which would yield the opaque `[object Object]` for plain
    // objects. `JSON.stringify` returns `undefined` for symbols and functions
    // and throws on BigInt or circular references; both fall back to a static
    // sentinel.
    try {
      return JSON.stringify(cause) ?? "[unserializable cause]";
    } catch {
      return "[unserializable cause]";
    }
  }

  private constructor(
    public readonly message: string,
    public readonly name?: string,
    public readonly stack?: string,
    public readonly cause?: string | SerializableError,
  ) {}

  toError(): Error {
    const cause =
      this.cause instanceof SerializableError
        ? this.cause.toError()
        : this.cause;
    const error = new Error(this.message, { cause });
    if (this.name) {
      error.name = this.name;
    }
    if (this.stack) {
      // Skip the assignment when stack is absent — V8 captures a stack at
      // construction, and assigning `undefined` would replace that with
      // nothing, leaving Sentry's parseStackFrames with no frames to read.
      error.stack = this.stack;
    }
    return error;
  }

  toJSON(): ErrorDetail {
    return {
      message: this.message,
      name: this.name,
      stack: this.stack,
      cause:
        this.cause instanceof SerializableError
          ? this.cause.toJSON()
          : this.cause,
    };
  }
}

export interface MessagePayload<T = unknown, D = unknown> {
  correlationId: string;
  data: D;
  debug?: boolean;
  error?: ErrorDetail;
  signalPort: MessagePort;
  source: string;
  type: T;
}

export function isMessageEvent<T = unknown, D = unknown>(
  event: Event,
): event is MessageEvent<MessagePayload<T, D>> {
  return "data" in event;
}

export interface CommandMessage<T = unknown, D = unknown>
  extends Omit<MessagePayload, "correlationId" | "signalPort"> {
  correlationId?: string;
  data: D;
  signal?: AbortSignal;
  transferables?: Transferable[];
  type: T;
}

export interface DetachPortCommand {
  data: "detach";
}

export function isDetachPortCommand(
  event: Event,
): event is MessageEvent<DetachPortCommand> {
  return "data" in event && event.data === "detach";
}
